/**
 * Porta de entrada do i18n: o provedor, o gancho `useT` e o que as telas usam.
 *
 * Tela nenhuma importa o dicionário direto. Ela pede `useT()` e escreve chaves,
 * e é só isso que precisa saber. Quem quiser texto fora de componente usa o `t`
 * solto do núcleo, que este arquivo reexporta.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  IDIOMA_PADRAO,
  definirIdiomaAtivo,
  idiomaAtivo,
  traduzir,
  type Idioma,
  type Traduzir,
} from "./nucleo";
import { guardarIdioma, idiomaInicial } from "./preferencia";

export {
  IDIOMAS,
  IDIOMA_PADRAO,
  NOMES_DOS_IDIOMAS,
  conquistaDoMotor,
  contagemRegressiva,
  dataDaRodada,
  dataEHora,
  dataLonga,
  dinheiro,
  fatorDaPartida,
  lanceDaPartida,
  mensagemDoCodigo,
  motivoDoMotor,
  notaDeLaudo,
  mancheteDoMotor,
  noticiaDoMotor,
  numero,
  problemaDeDatapack,
  rotuloDeCaracteristica,
  rotuloDeFase,
  rotuloDePe,
  rotuloDePersonalidade,
  rotuloDeReputacao,
  rotuloDoMotor,
  rotuloDePosicao,
  rotuloDeResultado,
  rotuloDeSetor,
  t,
  tempoRelativo,
  textoDaSessao,
  traduzir,
} from "./nucleo";
export { idiomaDaEtiqueta, idiomaDoNavegador, idiomaGuardado, idiomaInicial } from "./preferencia";
export type { Chave, Dicionario, Idioma, Traduzir, Valores, ValoresDoMotor } from "./nucleo";

interface ValorDoContexto {
  idioma: Idioma;
  t: Traduzir;
  definirIdioma: (idioma: Idioma) => void;
}

/**
 * Contexto já nasce em português.
 *
 * Sem provedor por perto, `useT` continua devolvendo texto. Isso mantém um teste
 * de componente solto funcionando sem precisar montar a árvore inteira do jogo.
 */
const Contexto = createContext<ValorDoContexto>({
  idioma: IDIOMA_PADRAO,
  t: (chave, valores) => traduzir(chave, valores, IDIOMA_PADRAO),
  definirIdioma: () => {},
});

/**
 * O idioma da árvore inteira.
 *
 * Sem `idioma` explícito, o jogo abre no que a pessoa já escolheu neste
 * aparelho e, na primeira vez, no do navegador. O parâmetro continua existindo
 * para quem monta a árvore num teste e quer um idioma fixo.
 */
export function ProvedorDeIdioma({
  idioma: inicial,
  children,
}: {
  idioma?: Idioma;
  children: ReactNode;
}): ReactNode {
  const [idioma, trocar] = useState<Idioma>(() => inicial ?? idiomaInicial());

  // Espelha o idioma no núcleo para o `t` solto ver o mesmo que os componentes.
  // É uma cópia de valor, sem efeito colateral nenhum, então pode acontecer no
  // corpo: esperar um efeito deixaria o primeiro desenho no idioma anterior.
  if (idiomaAtivo() !== idioma) definirIdiomaAtivo(idioma);

  // O `lang` do documento é o que faz o navegador hifenizar, corrigir ortografia
  // e ler em voz alta no idioma certo. Custa uma linha e ninguém lembra dele.
  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = idioma;
  }, [idioma]);

  /*
   * Trocar é gravar. A escolha precisa sobreviver a fechar o jogo, e gravar
   * aqui, e não em cada tela que oferecer a troca, é o que garante que duas
   * telas não guardem a mesma coisa de dois jeitos.
   */
  const definirIdioma = useCallback((escolhido: Idioma) => {
    guardarIdioma(escolhido);
    trocar(escolhido);
  }, []);

  const valor = useMemo<ValorDoContexto>(
    () => ({
      idioma,
      t: (chave, valores) => traduzir(chave, valores, idioma),
      definirIdioma,
    }),
    [idioma, definirIdioma],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/** A função de tradução no idioma atual. É o que as telas usam. */
export function useT(): Traduzir {
  return useContext(Contexto).t;
}

/** Idioma atual e como trocá-lo, para uma futura tela de opções. */
export function useIdioma(): { idioma: Idioma; definirIdioma: (idioma: Idioma) => void } {
  const { idioma, definirIdioma } = useContext(Contexto);
  return { idioma, definirIdioma };
}
